import React, { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import FilterChip from "../components/FilterChip";
import TicketCard from "../components/TicketCard";
import EventDetailDialog from "../components/EventDetailDialog";
import ContactDialog from "../components/ContactDialog";
import { fetchEvents, fetchRegions, fetchCountries } from "../lib/api";
import { tr } from "../lib/i18n";
import { Sparkles, MessageCircle } from "lucide-react";

const CATEGORIES = ["all", "letenky", "zajezd", "vylet", "koncert", "festival", "sraz"];

export default function EventsPage() {
  const [lang, setLang] = useState(() => localStorage.getItem("cy_lang") || "cz");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [hotOnly, setHotOnly] = useState(false);

  const [events, setEvents] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEvent, setOpenEvent] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => localStorage.setItem("cy_lang", lang), [lang]);

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([fetchRegions(), fetchCountries()]);
        setRegions(r);
        setCountries(c);
      } catch (e) {
        console.error("Failed to load meta", e);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = {};
        if (category !== "all") params.category = category;
        if (country !== "all") params.country = country;
        if (region !== "all") params.region = region;
        if (search.trim()) params.search = search.trim();
        const data = await fetchEvents(params);
        if (!cancelled) setEvents(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [category, country, region, search]);

  const visible = useMemo(
    () => (hotOnly ? events.filter((e) => e.is_hot) : events),
    [events, hotOnly]
  );

  const showRegionFilter = country === "CZ" || country === "all";

  const regionName = (r) => r[`name_${lang === "cz" ? "cz" : lang === "sk" ? "sk" : "en"}`];
  const countryName = (c) => c[`name_${lang === "cz" ? "cz" : lang === "sk" ? "sk" : "en"}`];

  const clearFilters = () => {
    setCategory("all"); setCountry("all"); setRegion("all"); setHotOnly(false); setSearch("");
  };

  const hasFilters =
    category !== "all" || country !== "all" || region !== "all" || hotOnly || search.trim();

  return (
    <div className="min-h-screen bg-paper">
      <TopNav lang={lang} setLang={setLang} search={search} setSearch={setSearch} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-20">
        {/* TAGLINE (not a hero, just a thin signal) */}
        <div className="flex items-center gap-2 mb-6 sm:mb-8">
          <Sparkles className="w-4 h-4 text-sky-500" />
          <p className="text-sm sm:text-base font-semibold text-slate-700">
            <span className="font-heading font-extrabold text-slate-900">{tr(lang, "tagline")} </span>
            <span className="text-slate-500">{tr(lang, "subtitle")}</span>
          </p>
        </div>

        {/* FILTERS - Category */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mr-1">
            {tr(lang, "filters.category")}
          </span>
        </div>
        <div data-testid="filter-row-category" className="flex flex-nowrap overflow-x-auto pb-2 gap-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {CATEGORIES.map((c) => (
            <FilterChip
              key={c}
              testid={`filter-chip-category-${c}`}
              active={category === c}
              onClick={() => setCategory(c)}
            >
              {tr(lang, `categories.${c}`)}
            </FilterChip>
          ))}
          <FilterChip
            testid="filter-chip-hot"
            active={hotOnly}
            onClick={() => setHotOnly((v) => !v)}
          >
            {tr(lang, "filters.hot")}
          </FilterChip>
        </div>

        {/* FILTERS - Country */}
        <div className="flex items-center gap-2 mt-5 mb-3">
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mr-1">
            {tr(lang, "filters.country")}
          </span>
        </div>
        <div data-testid="filter-row-country" className="flex flex-nowrap overflow-x-auto pb-2 gap-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          <FilterChip
            testid="filter-chip-country-all"
            active={country === "all"}
            onClick={() => { setCountry("all"); }}
          >
            🌍 {tr(lang, "filters.all")}
          </FilterChip>
          {countries.map((c) => (
            <FilterChip
              key={c.code}
              testid={`filter-chip-country-${c.code}`}
              active={country === c.code}
              onClick={() => { setCountry(c.code); if (c.code !== "CZ") setRegion("all"); }}
            >
              <span className="mr-1">{c.flag}</span>{countryName(c)}
            </FilterChip>
          ))}
        </div>

        {/* FILTERS - Region (only for CZ or all) */}
        {showRegionFilter && (
          <>
            <div className="flex items-center gap-2 mt-5 mb-3">
              <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mr-1">
                {tr(lang, "filters.region")}
              </span>
            </div>
            <div data-testid="filter-row-region" className="flex flex-nowrap overflow-x-auto pb-2 gap-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              <FilterChip
                testid="filter-chip-region-all"
                active={region === "all"}
                onClick={() => setRegion("all")}
              >
                {tr(lang, "filters.all")}
              </FilterChip>
              {regions.map((r) => (
                <FilterChip
                  key={r.code}
                  testid={`filter-chip-region-${r.code}`}
                  active={region === r.code}
                  onClick={() => setRegion(r.code)}
                >
                  {regionName(r)}
                </FilterChip>
              ))}
            </div>
          </>
        )}

        {hasFilters && (
          <button
            data-testid="filter-clear-btn"
            onClick={clearFilters}
            className="mt-4 text-xs font-bold text-sky-600 hover:text-sky-700 underline underline-offset-4"
          >
            {tr(lang, "filters.clear")}
          </button>
        )}

        {/* GRID */}
        <section data-testid="events-grid" className="mt-8 sm:mt-10">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 h-64 animate-pulse" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div data-testid="events-empty" className="text-center py-16 sm:py-24">
              <p className="font-heading font-bold text-2xl text-slate-900">
                {tr(lang, "empty.title")}
              </p>
              <p className="text-slate-500 mt-2 font-medium">{tr(lang, "empty.desc")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((e, i) => (
                <TicketCard key={e.id} event={e} lang={lang} index={i} onOpen={setOpenEvent} />
              ))}
            </div>
          )}
        </section>

        {/* FOOTER */}
        <footer className="mt-20 pt-10 border-t border-slate-200 text-center">
          <p className="font-heading font-bold text-slate-900">{tr(lang, "footer.tagline")}</p>
          <p className="text-xs text-slate-400 font-semibold mt-1.5 uppercase tracking-[0.15em]">
            {tr(lang, "footer.safety")}
          </p>
        </footer>
      </main>

      <EventDetailDialog event={openEvent} lang={lang} open={!!openEvent} onClose={() => setOpenEvent(null)} />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />

      {/* Floating Napiš nám button */}
      <button
        data-testid="floating-contact-btn"
        onClick={() => setContactOpen(true)}
        className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-40 group flex items-center gap-2 px-4 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-[0_18px_40px_-12px_rgba(15,23,42,0.6)] active:scale-95 transition-all"
        title="Napiš nám"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="hidden sm:inline text-sm">Napiš nám</span>
      </button>
    </div>
  );
}
